import { KSpan, KTooltip } from "kahuna-base-react-components"
import CalendarDate from "./CalendarDate"
import React from "react"
import { formatDate, getCurrency } from "../../../utility"
import { ReportData } from "./ReportsTable"
import { lang } from "../../../constants/languages"
import { UserAdvanceTypeTitle } from "../../../constants"
import DownloadReportButton from "../../layouts/royalties/reports/DownloadReportButton"

const ReportListComponent = ({
  i,
  row,
  isAdmin,
  selectedUserId,
  approveDisabled,
  advancePaid
}: {
  i: number
  row: ReportData
  isAdmin: boolean | undefined
  selectedUserId: number | undefined
  approveDisabled: boolean | undefined
  advancePaid: boolean
}) => {
  const divHeight = 40

  const getLocalRoyaltyString = (num?: number) => {
    return num?.toLocaleString("en-EN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const removeDuplicates = (data?: string[]) => {
    if (!data) return []
    const filteredDates = data.filter((value, index) => data.indexOf(value) === index && value !== "")
    if (filteredDates.length > 3) {
      return [filteredDates[0], "...", filteredDates[1]]
    }
    return filteredDates
  }

  const refactoredPaymentDays = removeDuplicates(row.payment_day)

  const groupByDate = (paymentList: { date: string; payment_amount: number }[]) => {
    if (!paymentList) return []
    const groupedPayments: { [date: string]: { totalAmount: number } } = {}

    paymentList.forEach((payment) => {
      const date = payment.date
      const amount = Number(payment.payment_amount) || 0

      if (!groupedPayments?.[date]) {
        groupedPayments[date] = { totalAmount: 0 }
      }
      groupedPayments[date].totalAmount += amount
    })

    return Object.entries(groupedPayments).map(([date, data]) => ({
      date,
      payment_amount: data.totalAmount
    }))
  }

  const advancePaymentList = groupByDate(row?.user_payment_details?.advance_payment_list)
  const regularPaymentList = groupByDate(row?.user_payment_details?.regular_payment_list)

  return (
    <li
      key={`data-row-${i}`}
      onClick={() => {
        // do nothing
      }}
    >
      <div className="table-row-hover hover:bg-[#F7F7F7] !border-none rounded-lg p-[12px] gap-[20px] h-[64px]">
        <div className={`h-[${divHeight}px] flex flex-row justify-between items-center `}>
          <div className="w-[20%] flex flex-row justify-start">
            <CalendarDate quarter={row.quarter} />
          </div>
          <div data-testid="reports-revenue" className="w-[15%] flex flex-row justify-start">
            {Object.keys(row.revenue_details).length > 0 ? (
              <KTooltip
                backgroundColor="#000"
                arrowColor="#000"
                position="bottom"
                content={
                  <div className="w-max flex flex-col gap-1 items-start p-0.5">
                    {Object.keys(row.revenue_details).map((key, index: number) => {
                      return (
                        <KSpan
                          key={`${key}-${index}`}
                          text={`${lang.reports.list_component.date}: ${key},  ${
                            lang.reports.list_component.revenue
                          }: ${row.revenue_details[key].toFixed(2)}`}
                          fontSize={12}
                          fontWeight={500}
                          lineHeight="16px"
                          color="#FFF"
                        />
                      )
                    })}
                  </div>
                }
              >
                <div className="flex justify-center p-1 w-auto rounded-[4px] bg-[#F7F7F7]">
                  <KSpan
                    text={`${getCurrency(row.currency)}${getLocalRoyaltyString(row.revenue)}`}
                    color="#111"
                    fontWeight={500}
                    letterSpacing="0.22px"
                    fontSize={11}
                    lineHeight="12px"
                  />
                </div>
              </KTooltip>
            ) : (
              <div className="flex justify-center p-1 w-auto rounded-[4px] bg-[#F7F7F7]">
                <KSpan
                  text={`${getCurrency(row.currency)}${getLocalRoyaltyString(row.revenue)}`}
                  color="#111"
                  fontWeight={500}
                  letterSpacing="0.22px"
                  fontSize={11}
                  lineHeight="12px"
                />
              </div>
            )}
          </div>
          <div className="w-[15%] flex flex-row justify-start">
            <KTooltip
              backgroundColor="#000"
              arrowColor="#000"
              position="bottom"
              content={
                <div className="w-max flex flex-col gap-1 items-start p-0.5">
                  {advancePaid && (
                    <KSpan
                      text={`${lang.reports.list_component.advance_deduction}: ${getCurrency(
                        row?.advance_currency
                      )}${getLocalRoyaltyString(row.user_payment_details.advance)}`}
                      fontSize={12}
                      fontWeight={500}
                      lineHeight="16px"
                      color="#FFF"
                    />
                  )}
                  {advancePaymentList.length > 0 && (
                    <div className="flex flex-col gap-1 pt-1 pb-1 pl-4">
                      {advancePaymentList.map((advance_payment, i) => {
                        return (
                          <KSpan
                            key={`advance-payment-${i}`}
                            text={`- ${lang.reports.list_component.report_date}: ${advance_payment?.date}, ${
                              lang.reports.list_component.amount
                            }: ${getLocalRoyaltyString(advance_payment?.payment_amount)}`}
                            fontSize={11}
                            fontWeight={500}
                            lineHeight="14px"
                            color="#FFF"
                          />
                        )
                      })}
                    </div>
                  )}
                  <KSpan
                    text={`${lang.reports.list_component.payout_amount}: ${getCurrency(
                      row.currency
                    )}${getLocalRoyaltyString(row.user_payment_details.regular)}`}
                    fontSize={12}
                    fontWeight={500}
                    lineHeight="16px"
                    color="#FFF"
                  />
                  {regularPaymentList.length > 0 && (
                    <div className="flex flex-col gap-1 pt-1 pb-1 pl-4">
                      {regularPaymentList.map((regular_payment, i) => {
                        return (
                          <KSpan
                            key={`regular-payment-${i}`}
                            text={`- ${lang.reports.list_component.report_date}: ${regular_payment?.date}, ${
                              lang.reports.list_component.amount
                            }: ${getLocalRoyaltyString(regular_payment?.payment_amount)}`}
                            fontSize={11}
                            fontWeight={500}
                            lineHeight="14px"
                            color="#FFF"
                          />
                        )
                      })}
                    </div>
                  )}
                </div>
              }
            >
              <div className="flex justify-center p-1 w-auto rounded-[4px] bg-[#F7F7F7]">
                <KSpan
                  text={`${getCurrency(row.currency)}${getLocalRoyaltyString(row.user_payment_total)}`}
                  color="#111"
                  fontWeight={500}
                  letterSpacing="0.22px"
                  fontSize={11}
                  lineHeight="12px"
                />
              </div>
            </KTooltip>
          </div>
          <div className="w-[15%] flex flex-row gap-1 items-center justify-start">
            {row.payment_day && refactoredPaymentDays.length > 0 && (
              <div className="flex items-center justify-center">
                <img src="/mail-gray.svg" alt="mail-icon" />
              </div>
            )}
            <div className="flex flex-col justify-center items-start items-center">
              {row.payment_day && refactoredPaymentDays.length > 0 ? (
                <KTooltip
                  children={
                    <div className="flex flex-col justify-center items-start items-center">
                      {refactoredPaymentDays.map((payment, index) => {
                        return (
                          <KSpan
                            text={formatDate(payment)}
                            key={`payment-${index}`}
                            fontWeight={500}
                            color="#111"
                            letterSpacing="-0.084px"
                            fontSize={14}
                          />
                        )
                      })}
                    </div>
                  }
                  width="120px"
                  content={
                    <div className="flex flex-col items-start">
                      {[...row.payment_day].map((payment, index) => {
                        return (
                          <KSpan
                            text={`- ${formatDate(payment)}`}
                            key={`payment-${index}`}
                            fontWeight={500}
                            color="#111"
                            letterSpacing="-0.084px"
                            fontSize={14}
                          />
                        )
                      })}
                    </div>
                  }
                />
              ) : (
                <KSpan text="-" fontWeight={500} color="#111" letterSpacing="-0.084px" fontSize={14} />
              )}
            </div>
          </div>
          {advancePaid && (
            <div className="w-[10%] flex flex-row justify-start">
              {row.advance_paid_list.length > 0 ? (
                <KTooltip
                  backgroundColor="#000"
                  arrowColor="#000"
                  position="bottom"
                  marginLeft="-30px"
                  content={
                    <div className="w-max flex flex-col gap-1.5 items-start p-0.5">
                      {row.advance_paid_list.map((advanceItem, index: number) => {
                        return (
                          <KSpan
                            key={`${advanceItem.advance_paid}-${index}`}
                            text={`${lang.reports.list_component.date}: ${formatDate(advanceItem.date)},
                            ${lang.reports.list_component.type}: ${UserAdvanceTypeTitle[advanceItem.advance_type]},
                            ${lang.reports.list_component.amount}: ${getLocalRoyaltyString(advanceItem.advance_paid)}`}
                            fontSize={12}
                            fontWeight={500}
                            lineHeight="16px"
                            color="#FFF"
                          />
                        )
                      })}
                    </div>
                  }
                >
                  <div className="flex justify-center p-1 w-auto rounded-[4px] bg-[#F7F7F7]">
                    <KSpan
                      text={`${getCurrency(row.advance_currency)}${getLocalRoyaltyString(row.advance_paid)}`}
                      color="#111"
                      fontWeight={500}
                      letterSpacing="0.22px"
                      fontSize={11}
                      lineHeight="12px"
                    />
                  </div>
                </KTooltip>
              ) : (
                <div className="flex justify-center p-1 w-auto rounded-[4px] bg-[#F7F7F7]">
                  <KSpan
                    text={`${getCurrency(row.advance_currency)}${getLocalRoyaltyString(row.advance_paid)}`}
                    color="#111"
                    fontWeight={500}
                    letterSpacing="0.22px"
                    fontSize={11}
                    lineHeight="12px"
                  />
                </div>
              )}
            </div>
          )}
          {advancePaid && (
            <div className="w-[15%] flex flex-row justify-start">
              <div className="flex justify-center p-1 w-auto rounded-[4px] bg-[#F7F7F7]">
                <KSpan
                  text={`${getCurrency(row?.advance_currency)}${
                    row.balance_remaining && row.balance_remaining > 0
                      ? getLocalRoyaltyString(row.balance_remaining)
                      : "0.00"
                  }`}
                  color="#111"
                  fontWeight={500}
                  letterSpacing="0.22px"
                  fontSize={11}
                  lineHeight="12px"
                />
              </div>
            </div>
          )}
          <div className={`w-[5%] flex flex-row justify-end`}>
            <DownloadReportButton
              reportId={`${parseInt(row.quarter)}${row.quarter.split(" ")[1].toLowerCase()}`}
              isAdmin={isAdmin}
              selectedUserId={selectedUserId}
              approveDisabled={approveDisabled}
            />
          </div>
        </div>
      </div>
    </li>
  )
}

export default ReportListComponent
